import { captureException } from "@sentry/nextjs";
import type { AxiosError, AxiosResponse } from "axios";
import axios from "axios";
import type {
	NskResponse,
	NskResponseFail,
	NskResponseSuccess
} from "~/models/Api";
import ApiClient from "~/utils/api/api_client";

type ApiResponse<T = null> = {
	code?: number;
	data: T;
	message?: string;
};

export const isSuccess = (
	response: NskResponse<unknown>
): response is NskResponseSuccess<unknown> => response.type === "success";

const parseSuccess = <T>(
	response: AxiosResponse<T>
): NskResponseSuccess<T> => ({
	code: response.status ?? 204,
	data: response.data ?? null,
	type: "success"
});
const parseError = <E>(err: unknown): NskResponseFail<E> => {
	if (axios.isAxiosError(err)) {
		const axiosError: AxiosError<ApiResponse<E>> = err;
		if (axiosError?.response?.data?.message == null) {
			// 予期せぬエラー
			captureException(err);
		}
		return {
			code: axiosError?.response?.data?.code ?? null,
			data: axiosError?.response?.data?.data ?? null,
			message: axiosError?.response?.data?.message ?? null,
			type: "fail"
		};
	}
	throw err;
};

export const postRequest = async <T, E>(
	path: string,
	params?: Record<string, unknown> | FormData
): Promise<NskResponse<T, E>> => {
	try {
		return parseSuccess<T>(await ApiClient.post(path, params));
	} catch (err) {
		return parseError<E>(err);
	}
};

export const putRequest = async <T, E>(
	path: string,
	params?: Record<string, unknown> | FormData
): Promise<NskResponse<T, E>> => {
	try {
		return parseSuccess<T>(await ApiClient.put(path, params));
	} catch (err) {
		return parseError<E>(err);
	}
};

export const deleteRequest = async <T, E>(
	path: string,
	params?: Record<string, unknown>
): Promise<NskResponse<T, E>> => {
	try {
		return parseSuccess<T>(await ApiClient.delete(path, params));
	} catch (err) {
		return parseError<E>(err);
	}
};
